import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getGroupOptouts, toggleGroupOptout } from 'helpers/documentManagerApi';

const ManageTypeGroupsModal = ({ show, handleClose, type }) => {
  const [optedOutIds, setOptedOutIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  // Retrieve groups list from localStorage as populated on login
  const groupsList = JSON.parse(localStorage.getItem('groupsData')) || [];

  useEffect(() => {
    const fetchOptouts = async () => {
      if (!type) return;
      try {
        setLoading(true);
        const res = await getGroupOptouts(type.id);
        if (res && res.status) {
          // Normalize opted-out group IDs to handle either primitive arrays or arrays of objects
          const optouts = (res.data || []).map(item => {
            if (typeof item === 'object' && item !== null) {
              return Number(item.group_id ?? item.id ?? item.value);
            }
            return Number(item);
          });
          setOptedOutIds(optouts);
        } else {
          toast.error(res?.message || 'Failed to fetch opted-out groups');
        }
      } catch (err) {
        console.error(err);
        toast.error('Error fetching group configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchOptouts();
  }, [type]);

  const handleToggleGroup = async (groupId, currentEnabled) => {
    try {
      setSavingId(groupId);
      const newEnabled = !currentEnabled;
      const res = await toggleGroupOptout(type.id, groupId, newEnabled);
      if (res && res.status) {
        if (newEnabled) {
          // Toggled ON -> Remove from opt-out array
          setOptedOutIds(prev => prev.filter(id => id !== groupId));
          toast.success('Enabled document type for group');
        } else {
          // Toggled OFF -> Add to opt-out array
          setOptedOutIds(prev => [...prev, groupId]);
          toast.success('Disabled document type for group');
        }
      } else {
        toast.error(res?.message || 'Failed to update group setting');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error updating group setting');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-9">
          Manage Group Access — {type?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="p-3 modal-scroll-hidden"
        style={{
          height: '400px',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        <style>{`
          .modal-scroll-hidden::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-600 mb-0">Loading groups...</p>
          </div>
        ) : groupsList.length === 0 ? (
          <p className="text-muted text-center my-3">
            No facility groups found.
          </p>
        ) : (
          <div>
            <p className="text-600 fs-10 mb-3">
              By default, all document types are enabled for all groups. Toggle
              a switch off to disable this document type for that group.
            </p>
            {groupsList.map(group => {
              const isEnabled = !optedOutIds.includes(Number(group.value));
              const isSaving = savingId === group.value;

              return (
                <div
                  key={group.value}
                  className="d-flex align-items-center justify-content-between p-2 mb-2 border rounded bg-card"
                >
                  <span className="fw-semi-bold fs-10">{group.key}</span>
                  <div className="d-flex align-items-center gap-2">
                    {isSaving && (
                      <Spinner
                        animation="border"
                        size="sm"
                        variant="secondary"
                      />
                    )}
                    <Form.Check
                      type="switch"
                      id={`group-switch-${group.value}`}
                      checked={isEnabled}
                      disabled={isSaving}
                      onChange={() =>
                        handleToggleGroup(Number(group.value), isEnabled)
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ManageTypeGroupsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  type: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })
};

export default ManageTypeGroupsModal;
